import type { ConnectionCard } from "../../../api/types";
import { dateTime, relative } from "../../../lib/format";
import { CopyButton } from "../CopyButton";
import styles from "./connection.module.css";

type Push = NonNullable<ConnectionCard["push"]>;

/** The one request an integrator needs to prove the connection: an empty batch is still a push. */
export function sampleRequest(endpoint: string): string {
  return [
    `curl -X POST "${endpoint}/documents" \\`,
    `  -H "Authorization: Bearer <push token>" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"batchId":"first-push","items":[]}'`,
  ].join("\n");
}

function When({ at }: { at: string | null | undefined }) {
  if (!at) return <>—</>;
  return <span title={dateTime(at)}>{relative(at)}</span>;
}

/** Method 4 — Push API: where the ERP sends its batches, and what the hub has received. */
export function PushPanel({
  push,
  lastContactAt,
}: {
  push: Push;
  lastContactAt: string | null | undefined;
}) {
  const request = sampleRequest(push.endpoint);
  return (
    <section className="card card-tight" aria-label="Push API">
      <div className="card-head">
        <h2 className="grow">Push API</h2>
      </div>
      <div className="card-body">
        <p className={styles.meta}>The ERP sends invoices to the hub in batches. The hub does not connect to the ERP.</p>
        <div className={styles.tokenBox}>
          <label htmlFor="push-endpoint">Endpoint</label>
          <div className={styles.tokenRow}>
            <input id="push-endpoint" className="input mono" type="text" readOnly value={push.endpoint} />
            <CopyButton value={push.endpoint} label="Copy the endpoint" />
          </div>
          <label htmlFor="push-sample">Sample request</label>
          <div className={styles.tokenRow}>
            <textarea id="push-sample" className="input mono" readOnly rows={4} value={request} />
            <CopyButton value={request} label="Copy the sample request" />
          </div>
        </div>
      </div>
      <div className="table-wrap">
        <table className="tbl" aria-label="Push numbers">
          <tbody>
            <tr>
              <th scope="row">Last push</th>
              <td><When at={lastContactAt} /></td>
            </tr>
            <tr>
              <th scope="row">Invoices received today</th>
              <td>{push.receivedToday}</td>
            </tr>
            <tr>
              <th scope="row">Backlog</th>
              <td>{push.backlog}</td>
            </tr>
            <tr>
              <th scope="row">Results not yet acknowledged</th>
              <td>{push.unackedResults}</td>
            </tr>
            <tr>
              <th scope="row">Last reconciliation</th>
              <td>
                {push.lastReconcile ? (
                  <>
                    <When at={push.lastReconcile.at} />
                    {`, ${push.lastReconcile.missing} missing of ${push.lastReconcile.sent}`}
                  </>
                ) : (
                  "Not yet"
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
